import { useCallback, useEffect, useRef } from 'react';
import { datadog } from '@sg/expo-datadog-client';

import { useLaunchDarkly } from '@order/LaunchDarkly';

import { legacy_CUSTOM_ATTRIBUTES } from './useLaunchDarklyIntegration.constants';
import { type Customer } from './useLaunchDarklyIntegration.types';
import { generateInitialContext } from './useLaunchDarklyIntegration.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useLaunchDarklyIntegration = (params: Params) => {
  const { customer, isLoggingIn, hasLoggedOut } = params;

  // ─────────────────────────────────────────────────────────────────────

  const {
    isLaunchDarklyReady,
    isLaunchDarklyTargetingContextSet,
    identifyCustomer: identifyLaunchDarklyCustomer,
    resetCustomer: resetLaunchDarklyCustomer,
    addContexts: addLaunchDarklyContexts,
  } = useLaunchDarkly();

  // ─── Refs ────────────────────────────────────────────────────────────

  const identifiedCustomerKeyRef = useRef<string | undefined>(undefined);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setInitialContext = useCallback(() => {
    const canInitialize = isLaunchDarklyReady && !isLaunchDarklyTargetingContextSet && !isLoggingIn; // prettier-ignore

    if (!canInitialize) {
      return;
    }

    const initialContext = generateInitialContext(customer);

    identifiedCustomerKeyRef.current = initialContext.user?.key;

    datadog.addAction('[Launch Darkly Integration] Set Initial Context', {
      ...initialContext,
      user: {
        key: initialContext?.user?.key ?? '-',
        isAnonymous: !initialContext?.user?.key,
      },
    });

    void addLaunchDarklyContexts(initialContext);
  }, [
    customer,
    isLoggingIn,
    isLaunchDarklyReady,
    isLaunchDarklyTargetingContextSet,
    addLaunchDarklyContexts,
  ]);

  const setUserContext = useCallback(() => {
    const customerId = customer.id;
    const isSignedIn = typeof customerId === 'string';
    const isAlreadyIdentified = identifiedCustomerKeyRef.current === customerId;
    const canIdentify = isLaunchDarklyReady && isLaunchDarklyTargetingContextSet && isSignedIn && !isAlreadyIdentified; // prettier-ignore

    if (!canIdentify) {
      return;
    }

    const userContext = { ...customer, id: customerId };

    identifiedCustomerKeyRef.current = customerId;

    datadog.addAction('[Launch Darkly Integration] Set User Context', {
      key: userContext?.id ?? '-',
      isAnonymous: !userContext?.id,
    });

    void identifyLaunchDarklyCustomer(userContext, legacy_CUSTOM_ATTRIBUTES);
  }, [
    customer,
    identifyLaunchDarklyCustomer,
    isLaunchDarklyReady,
    isLaunchDarklyTargetingContextSet,
  ]);

  const resetUserContext = useCallback(() => {
    if (!hasLoggedOut) {
      return;
    }

    identifiedCustomerKeyRef.current = undefined;

    datadog.addAction('[Launch Darkly Integration] Reset User Context');

    void resetLaunchDarklyCustomer(legacy_CUSTOM_ATTRIBUTES);
  }, [hasLoggedOut, resetLaunchDarklyCustomer]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(setInitialContext, [setInitialContext]);
  useEffect(setUserContext, [setUserContext]);
  useEffect(resetUserContext, [resetUserContext]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  customer: Customer;
  isLoggingIn: boolean;
  hasLoggedOut: boolean;
};
